import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Module } from '../../../api/module';
import { ModalContextProvider } from '../../../contexts/ModalContext';
import useLicenses from '../../../hooks/useLicenses';
import { useRoutes } from '../../../hooks/useRoutes';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import ModuleLicenseAlert from '../../../shared-components/alerts/ModuleLicenseAlert';
import { FilterSearchParam } from '../../../shared-components/elementis/types/FilterSearchParam';
import TABS from '../../../shared-components/elementis/types/ResultsPageTabs';
import { ExerciseCategoriesContextProvider } from '../_contexts/ExerciseCategoriesContext';
import ModuleContext from '../_contexts/ModuleContext';
import SuperModuleBreadcrumb from '../components/breadcrumbs/SuperModuleBreadcrumb';
import { TOCDataContextProvider } from './components/TOCDataContext';
import { FilterContextProvider } from './context/filter-context/FilterContext';
import { ResultsDataContextProvider } from './context/ResultsDataContext';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../core/access/module-access';

type TabId = typeof TABS[keyof typeof TABS];

function getLicenseValidationTypeForAllModules(
  allModules: Module[],
  getUserLicenseValidationType: ReturnType<typeof useLicenses>['getUserLicenseValidationType'],
): ReturnType<typeof getUserLicenseValidationType> | undefined {
  let validationType;
  allModules.forEach(module => {
    const type = getUserLicenseValidationType(module);
    if (type !== 'LICENSED') {
      validationType = type;
      return;
    }
  });
  return validationType;
}

function Results() {
  const { t } = useTranslation();
  const { getUserLicenseValidationType } = useLicenses();
  const { allModules, module: superModule } = useContext(ModuleContext);
  const location = useLocation();
  const { resultsAccess } = useModuleAccess(superModule);
  const { getRoute } = useRoutes();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const selectedTab: TabId | undefined = location.pathname.includes(getRoute('MATERIAL.RESULTS.BY_ITEM'))
    ? TABS.EXERCISE
    : TABS.STUDENT;

  const onTabChange = useCallback(
    (tab: string) => {
      if (tab === selectedTab) return;

      const route =
        tab === TABS.EXERCISE ? getRoute('MATERIAL.RESULTS.BY_ITEM') : getRoute('MATERIAL.RESULTS.BY_STUDENT');

      navigate(route);

      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(FilterSearchParam.Groups);
        newParams.delete(FilterSearchParam.Members);
        newParams.delete(FilterSearchParam.Exercises);

        return newParams;
      });
    },
    [selectedTab, setSearchParams, getRoute, navigate],
  );

  const validationType = getLicenseValidationTypeForAllModules(allModules, getUserLicenseValidationType);

  if (superModule && resultsAccess === FEATURE_ACCESS.HIDDEN) return <Navigate to=".." replace />;

  return (
    <Page>
      <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
        <ContentHeader
          cover={
            <Page.Header
              breadcrumbs={
                <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                  <SuperModuleBreadcrumb />
                </Breadcrumbs>
              }
              headerText={t('PAGES.RESULTS.PAGE_TITLE')}
              tabs={
                <Tabs.TabList aria-label={t('PAGES.RESULTS.TAB_LIST.A11Y_LABEL')}>
                  <Tabs.Tab id={TABS.STUDENT}>{t('PAGES.RESULTS.TAB_LIST.TABS.STUDENTS')}</Tabs.Tab>
                  <Tabs.Tab id={TABS.EXERCISE}>{t('PAGES.RESULTS.TAB_LIST.TABS.EXERCISE')}</Tabs.Tab>
                </Tabs.TabList>
              }
            />
          }
        />
        <ContentBody
          alerts={
            validationType ? (
              <ModuleLicenseAlert message={t(`SHARED_COMPONENTS.ALERTS.AGGREGATED.${validationType}`)} />
            ) : null
          }
          scrollableTable
        >
          <ExerciseCategoriesContextProvider>
            <ResultsDataContextProvider>
              <FilterContextProvider selectedTab={selectedTab}>
                <TOCDataContextProvider>
                  <ModalContextProvider>
                    <Tabs.TabsContent>
                      {Object.values(TABS).map(tabId => {
                        return (
                          <Tabs.TabPanel key={tabId} id={tabId}>
                            <Outlet />
                          </Tabs.TabPanel>
                        );
                      })}
                    </Tabs.TabsContent>
                  </ModalContextProvider>
                </TOCDataContextProvider>
              </FilterContextProvider>
            </ResultsDataContextProvider>
          </ExerciseCategoriesContextProvider>
        </ContentBody>
      </Tabs>
    </Page>
  );
}

export default Results;
